import * as Yup from 'yup'

type ProfileTopicApi = {
  code: string
  nameDe?: string
}

export interface ProfileSettingsData {
  salutation?: string
  firstName?: string
  lastName?: string
  company?: string
  street?: string
  streetNumber?: string
  postal?: string
  city?: string
  country?: string
  phonePrefix?: string
  phone?: string
  mobilePrefix?: string
  mobile?: string
  email?: string
  workingArea?: string
  workRadius?: string
  salesRegion?: string
  topics?: string[]
}

export interface ProfileSettingsApiData extends Omit<ProfileSettingsData, 'topics'> {
  topics?: Array<string | ProfileTopicApi>
}

export type ProfileSettingsKeys = keyof ProfileSettingsData

export const INITIAL_PROFILE_SETTINGS_VALUES: ProfileSettingsData = {
  salutation: '',
  firstName: '',
  lastName: '',
  company: '',
  street: '',
  streetNumber: '',
  postal: '',
  city: '',
  country: '',
  phonePrefix: '',
  phone: '',
  mobilePrefix: '',
  mobile: '',
  workingArea: '',
  workRadius: '',
  salesRegion: '',
  topics: [],
}

export const DEFAULT_PROFILE_VALUES = {
  salutation: 'Herr',
  country: 'Deutschland',
  phonePrefix: '+49',
  mobilePrefix: '+49',
} as const

// Mapping between display values and API values for salutation
export const SALUTATION_DISPLAY_TO_API: Record<string, string> = {
  Herr: 'm',
  Frau: 'f',
  Andere: 'o',
}

export const SALUTATION_API_TO_DISPLAY: Record<string, string> = {
  m: 'Herr',
  f: 'Frau',
  o: 'Andere',
}

// Mapping between display values and API values for country
export const COUNTRY_DISPLAY_TO_API: Record<string, string> = {
  Deutschland: 'de',
  Österreich: 'at',
  Schweiz: 'ch',
}

export const COUNTRY_API_TO_DISPLAY: Record<string, string> = {
  de: 'Deutschland',
  at: 'Österreich',
  ch: 'Schweiz',
}

const NAME_REGEX = /^[a-zA-ZäöüßÄÖÜàáâãèéêëìíîïòóôõùúûýÿçñÀÁÂÃÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÝŸÇÑ\s\-']+$/

const COMPANY_REGEX =
  /^[a-zA-Z0-9äöüßÄÖÜàáâãèéêëìíîïòóôõùúûýÿçñÀÁÂÃÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÝŸÇÑ\s\-&.,'()/]+$/

const STREET_REGEX =
  /^(?=.*[a-zA-Z0-9äöüßÄÖÜàáâãèéêëìíîïòóôõùúûýÿçñÀÁÂÃÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÝŸÇÑ])[a-zA-Z0-9äöüßÄÖÜàáâãèéêëìíîïòóôõùúûýÿçñÀÁÂÃÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÝŸÇÑ\s\-./]*$/

const CITY_REGEX =
  /^(?=.*[a-zA-ZäöüßÄÖÜàáâãèéêëìíîïòóôõùúûýÿçñÀÁÂÃÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÝŸÇÑ])[a-zA-ZäöüßÄÖÜàáâãèéêëìíîïòóôõùúûýÿçñÀÁÂÃÈÉÊËÌÍÎÏÒÓÔÕÙÚÛÝŸÇÑ\s\-'.]*$/

const STREET_NUMBER_REGEX = /^[0-9]+[a-zA-Z]?([\-\/][0-9]+[a-zA-Z]?)?$/

const POSTAL_REGEX = /^[0-9]{4,6}$/

const PHONE_REGEX = /^[0-9\s\-\/()]{6,20}$/
const trimString = (value: any, originalValue: any) =>
  typeof originalValue === 'string' ? originalValue.trim() : originalValue

export const ProfileSettingsSchema = Yup.object({
  salutation: Yup.string()
    .oneOf(['Herr', 'Frau', 'Andere'], 'Bitte wählen Sie eine Anrede')
    .required('Anrede ist erforderlich'),

  firstName: Yup.string()
    .transform(trimString)
    .matches(NAME_REGEX, {
      message: 'Vorname enthält ungültige Zeichen',
      excludeEmptyString: true,
    })
    .max(50, 'Vorname ist zu lang')
    .required('Vorname ist erforderlich'),

  lastName: Yup.string()
    .transform(trimString)
    .matches(NAME_REGEX, {
      message: 'Nachname enthält ungültige Zeichen',
      excludeEmptyString: true,
    })
    .max(50, 'Nachname ist zu lang')
    .required('Nachname ist erforderlich'),

  company: Yup.string()
    .transform(trimString)
    .matches(COMPANY_REGEX, {
      message: 'Unternehmen enthält ungültige Zeichen',
      excludeEmptyString: true,
    })
    .max(120, 'Unternehmen ist zu lang')
    .nullable()
    .optional(),

  street: Yup.string()
    .transform(trimString)
    .matches(STREET_REGEX, {
      message: 'Straße enthält ungültige Zeichen',
      excludeEmptyString: true,
    })
    .max(100, 'Straße ist zu lang')
    .required('Straße ist erforderlich'),

  streetNumber: Yup.string()
    .transform(trimString)
    .matches(STREET_NUMBER_REGEX, {
      message: 'Hausnummer muss mit einer Zahl beginnen (z. B. "13", "13a", "13-15", "13/1")',
      excludeEmptyString: true,
    })
    .max(20, 'Hausnummer ist zu lang')
    .required('Hausnummer ist erforderlich'),

  postal: Yup.string()
    .transform(trimString)
    .matches(POSTAL_REGEX, {
      message: 'Bitte geben Sie eine gültige Postleitzahl ein (4-6 Ziffern)',
      excludeEmptyString: true,
    })
    .required('Postleitzahl ist erforderlich'),

  city: Yup.string()
    .transform(trimString)
    .matches(CITY_REGEX, {
      message: 'Stadt enthält ungültige Zeichen',
      excludeEmptyString: true,
    })
    .max(100, 'Stadt ist zu lang')
    .required('Stadt ist erforderlich'),

  country: Yup.string()
    .oneOf(['Deutschland', 'Österreich', 'Schweiz'], 'Bitte wählen Sie ein Land')
    .required('Land ist erforderlich'),

  phonePrefix: Yup.string()
    .oneOf(['+49', '+43', '+41'], 'Bitte wählen Sie eine Vorwahl')
    .nullable()
    .optional(),

  phone: Yup.string()
    .nullable()
    .transform((value, originalValue) => {
      const v = typeof originalValue === 'string' ? originalValue.trim() : originalValue
      return v === '' ? null : v
    })
    .matches(PHONE_REGEX, {
      message: 'Bitte geben Sie eine gültige Telefonnummer ein (6-20 Zeichen)',
      excludeEmptyString: true,
    })
    .optional(),

  mobilePrefix: Yup.string()
    .oneOf(['+49', '+43', '+41'], 'Bitte wählen Sie eine Vorwahl')
    .nullable()
    .optional(),

  mobile: Yup.string()
    .nullable()
    .transform((value, originalValue) => {
      const v = typeof originalValue === 'string' ? originalValue.trim() : originalValue
      return v === '' ? null : v
    })
    .matches(PHONE_REGEX, {
      message: 'Bitte geben Sie eine gültige Mobilnummer ein (6-20 Zeichen)',
      excludeEmptyString: true,
    })
    .optional(),
})

// ---------------------------------------------------------------------------
// Deployment profile (Einsatzprofil) — SIB-731
// Backend (UserProfile): workingArea enum, workRadius (Integer, >0), salesRegion,
// topics (codes), and the shared address (postal/city/country) reused as the
// deployment starting point. All fields optional, except radius is required when
// the working area is CITY_RADIUS.
// ---------------------------------------------------------------------------

export const WORKING_AREA_VALUES = [
  'CITY_RADIUS',
  'FEDERAL_STATE',
  'GERMANY',
  'AUSTRIA',
  'SWITZERLAND',
] as const

export type WorkingArea = (typeof WORKING_AREA_VALUES)[number]

// Country-only working areas auto-fill the Land field with the matching country.
export const WORKING_AREA_TO_COUNTRY: Record<string, string> = {
  GERMANY: 'Deutschland',
  AUSTRIA: 'Österreich',
  SWITZERLAND: 'Schweiz',
}

// workingArea is a comma-separated list (the country areas can be multi-selected).
export const splitAreas = (area?: string) =>
  (area ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)

// Postal/city/country are required for the location-based working areas only.
const isLocationRequired = (area?: string) =>
  splitAreas(area).some(a => a === 'CITY_RADIUS' || a === 'FEDERAL_STATE')

export const DeploymentProfileSchema = Yup.object({
  workingArea: Yup.string()
    .test('valid-working-areas', 'Bitte wählen Sie einen Bereich', value =>
      splitAreas(value).every(v => (WORKING_AREA_VALUES as readonly string[]).includes(v))
    )
    .optional(),

  // Deployment starting point — reuses the shared address fields. Required for the
  // location-based working areas (CITY_RADIUS, FEDERAL_STATE), optional otherwise.
  postal: Yup.string()
    .transform(trimString)
    .matches(POSTAL_REGEX, {
      message: 'Bitte geben Sie eine gültige Postleitzahl ein (4-6 Ziffern)',
      excludeEmptyString: true,
    })
    .when('workingArea', {
      is: isLocationRequired,
      then: schema => schema.required('Postleitzahl ist erforderlich'),
      otherwise: schema => schema.optional(),
    }),

  city: Yup.string()
    .transform(trimString)
    .matches(CITY_REGEX, {
      message: 'Stadt enthält ungültige Zeichen',
      excludeEmptyString: true,
    })
    .max(100, 'Stadt ist zu lang')
    .when('workingArea', {
      is: isLocationRequired,
      then: schema => schema.required('Stadt ist erforderlich'),
      otherwise: schema => schema.optional(),
    }),

  country: Yup.string().when('workingArea', {
    is: isLocationRequired,
    then: schema =>
      schema
        .oneOf(['Deutschland', 'Österreich', 'Schweiz'], 'Bitte wählen Sie ein Land')
        .required('Land ist erforderlich'),
    otherwise: schema =>
      schema
        .test('valid-countries', 'Bitte wählen Sie ein Land', value =>
          (value ?? '')
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
            .every(v => ['Deutschland', 'Österreich', 'Schweiz'].includes(v))
        )
        .optional(),
  }),

  salesRegion: Yup.string().max(50, 'Vertriebsregion ist zu lang').optional(),

  // Stored as a string in the form (digit-sanitized on input).
  workRadius: Yup.string().when('workingArea', {
    is: (area: string) => splitAreas(area).includes('CITY_RADIUS'),
    then: schema =>
      schema
        .required('Bitte geben Sie einen Radius an')
        .test('radius-gt-0', 'Radius muss größer als 0 sein', value => Number(value) > 0),
    otherwise: schema =>
      schema
        .optional()
        .test(
          'radius-gt-0-optional',
          'Radius muss größer als 0 sein',
          value => !value || Number(value) > 0
        ),
  }),

  topics: Yup.array().of(Yup.string()).optional(),
})

export function normalizeProfileSettings(data: ProfileSettingsApiData): ProfileSettingsData {
  return {
    ...data,

    company: data.company ?? '',
    phone: data.phone ?? '',
    mobile: data.mobile ?? '',

    // Convert API salutation ('m', 'f', 'o') to display format ('Herr', 'Frau', 'Andere')
    salutation: data.salutation
      ? SALUTATION_API_TO_DISPLAY[data.salutation] || data.salutation
      : DEFAULT_PROFILE_VALUES.salutation,

    // Convert API country code(s) ('de', 'at', 'ch') to display format. The country
    // areas can be multi-selected, so this may be a comma-separated list.
    country: data.country
      ? String(data.country)
          .split(',')
          .map(c => c.trim())
          .filter(Boolean)
          .map(c => COUNTRY_API_TO_DISPLAY[c] || c)
          .join(',')
      : DEFAULT_PROFILE_VALUES.country,

    phonePrefix:
      data.phonePrefix && ['+49', '+43', '+41'].includes(data.phonePrefix)
        ? data.phonePrefix
        : DEFAULT_PROFILE_VALUES.phonePrefix,

    mobilePrefix:
      data.mobilePrefix && ['+49', '+43', '+41'].includes(data.mobilePrefix)
        ? data.mobilePrefix
        : DEFAULT_PROFILE_VALUES.mobilePrefix,

    // Backend returns workRadius as a number; the form treats it as a string.
    workRadius:
      data.workRadius !== null && data.workRadius !== undefined && data.workRadius !== ''
        ? String(data.workRadius)
        : '',

    topics: Array.isArray(data.topics)
      ? data.topics.map(topic => (typeof topic === 'string' ? topic : topic.code))
      : [],
  }
}

const CLEARABLE_FIELDS: Array<keyof ProfileSettingsData> = [
  'company',
  'phone',
  'mobile',
  // add more optional fields you want to be able to clear
]

export function profileSettingsToAPI(data: Partial<ProfileSettingsData>): Record<string, any> {
  const apiData: Record<string, any> = {}

  Object.keys(data).forEach(k => {
    const key = k as keyof ProfileSettingsData
    let value = data[key] as any

    if (typeof value === 'string') value = value.trim()

    // If user cleared an optional field, send null so backend clears it
    if (value === '' && CLEARABLE_FIELDS.includes(key)) {
      apiData[key] = ''
      return
    }

    // Otherwise: send non-empty values only
    if (value !== '' && value !== null && value !== undefined) {
      apiData[key] = value
    }
  })

  if (apiData.salutation && SALUTATION_DISPLAY_TO_API[apiData.salutation]) {
    apiData.salutation = SALUTATION_DISPLAY_TO_API[apiData.salutation]
  }

  if (apiData.country) {
    apiData.country = String(apiData.country)
      .split(',')
      .map(c => c.trim())
      .filter(Boolean)
      .map(c => COUNTRY_DISPLAY_TO_API[c] || c)
      .join(',')
  }
  return apiData
}
